import { app, auth, db } from '../firebase';
import {
  collection,
  doc,
  writeBatch,
  runTransaction,
  serverTimestamp,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  getDoc,
  setDoc,
  arrayUnion,
  arrayRemove,
  collectionGroup,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Bubble from '../models/Bubble';
import BubbleNode from '../models/BubbleNode';
import BubbleEdge from '../models/BubbleEdge';
import User from '../models/User';
import { MAX_USER_BUBBLES } from './bubbleSwitcher';
import { MEMO_TYPE, createFamilyMemo } from './memos';
import { buildCheckInMemo } from './checkin';
import { prepareStatusMemoMedia } from './memoMedia';
import { canJoinAtMemberCap, joinLimitMessage, memberLimitForPlan } from './billing';
import { INVITE_ALREADY_USED, inviteFromRecords } from './invites';
import { getPlaceWatcher } from './places/watcher';

// ============================================================================
// REAL BACKEND - FIREBASE
// ============================================================================
// 
// 🧠 THE CORE PHILOSOPHY: Graph-Based Family Galaxy System
//
// A Bubble = a family universe created by a bubble owner (the sun)
// A Node = a person inside a bubble (can be owner or participant)
// An Edge = a relationship link that connects two nodes
//
// Every action is either:
// 1. CREATING a bubble (becoming the sun, tier 1, owner)
// 2. JOINING a bubble via edges (becoming a planet, tier 2+, participant)
//
// Multi-Universe: Users can own multiple bubbles and join multiple bubbles
// Each node represents the user's identity in that specific bubble universe
// Tier system: Tier 1 = owner, Tier 2 = immediate family, Tier 3+ = extended
// ============================================================================

const STORAGE_BUCKETS = [
  'familybubble-ecfa6.appspot.com',
  'familybubble-ecfa6.firebasestorage.app',
];
const MAX_PHOTO_DATA_URL_CHARS = 350000;

const uploadToStorage = async (imageFile, userId, bucket, timeoutMs) => {
  const bucketStorage = getStorage(app, `gs://${bucket}`);
  const uniqueId = Date.now();
  const path = `user_photos/${userId}/${uniqueId}`;
  const imageRef = ref(bucketStorage, path);
  console.log("Attempting Storage upload:", bucket || '(default)', path);
  const uploadPromise = (async () => {
    const snapshot = await uploadBytes(imageRef, imageFile, {
      contentType: imageFile.type || 'image/jpeg',
    });
    return getDownloadURL(snapshot.ref);
  })();
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => reject(new Error('Photo upload timed out')), timeoutMs);
  });
  return Promise.race([uploadPromise, timeoutPromise]);
};

const fileToCompressedDataUrl = async (imageFile) => {
  if (typeof document === 'undefined' || typeof createImageBitmap !== 'function') {
    throw new Error('Photo compression is only available in the browser.');
  }
  const bitmap = await createImageBitmap(imageFile);
  try {
    const maxDim = 384;
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Could not prepare photo.');
    ctx.fillStyle = '#111827';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);
    let quality = 0.74;
    let dataUrl = canvas.toDataURL('image/jpeg', quality);
    while (dataUrl.length > MAX_PHOTO_DATA_URL_CHARS && quality > 0.35) {
      quality = Math.max(0.35, quality - 0.1);
      dataUrl = canvas.toDataURL('image/jpeg', quality);
    }
    if (dataUrl.length > MAX_PHOTO_DATA_URL_CHARS) {
      throw new Error('Photo is too large after compression. Try a smaller image.');
    }
    return dataUrl;
  } finally {
    if (typeof bitmap.close === 'function') bitmap.close();
  }
};

const uploadImage = async (imageFile, userId, timeoutMs = 12000) => {
  if (!imageFile) return null;
  if (!userId) {
    console.warn("No userId provided for image upload, skipping");
    return null;
  }

  const fileWithType =
    imageFile.type && imageFile.type.startsWith('image/')
      ? imageFile
      : new File([imageFile], imageFile.name || 'photo.jpg', { type: 'image/jpeg' });

  try {
    const downloadURL = await Promise.any(
      STORAGE_BUCKETS.map((bucket) => uploadToStorage(fileWithType, userId, bucket, timeoutMs))
    );
    console.log("Image uploaded to Firebase Storage:", downloadURL);
    return downloadURL;
  } catch (error) {
    console.warn("Firebase Storage upload failed on all buckets:", error?.errors?.[0]?.message || error.message);
  }

  try {
    const dataUrl = await fileToCompressedDataUrl(fileWithType);
    console.warn("Firebase Storage is not ready; stored a compressed photo in Firestore instead.");
    return dataUrl;
  } catch (error) {
    console.error("Error preparing photo:", error);
    return null;
  }
};

const normalizeInviteToken = (value) => (value || '').toString().trim().toUpperCase();

const inviteTokenCandidates = (rawToken) => {
  const raw = (rawToken || '').toString().trim();
  if (!raw) return [];
  return [...new Set([raw, raw.toUpperCase(), raw.toLowerCase()])];
};

const invitationFromDoc = (invitationDoc, edgeDoc = null) => {
  const invitation = invitationDoc.data() || {};
  return {
    token: invitation.token || invitation.inviteCode || null,
    bubbleId: invitation.bubbleId,
    fromNodeId: invitation.fromNodeId || invitation.referredNode || edgeDoc?.data()?.fromNode,
    edgeDoc,
    invitationDoc,
    accepted: inviteFromRecords(invitation, edgeDoc?.data()),
  };
};

const edgeInviteFromDoc = (edgeDoc) => {
  const data = edgeDoc.data() || {};
  return {
    token: data.referralToken || null,
    bubbleId: edgeDoc.ref.parent.parent.id,
    fromNodeId: data.fromNode,
    edgeDoc,
    invitationDoc: null,
    accepted: inviteFromRecords({}, data),
  };
};

const findEdgeInBubble = async (bubbleId, tokens) => {
  for (const token of tokens) {
    try {
      const edgeSnap = await getDocs(
        query(
          collection(db, 'bubbles', bubbleId, 'edges'),
          where('referralToken', '==', token)
        )
      );
      if (!edgeSnap.empty) return edgeSnap.docs[0];
    } catch (edgeError) {
      console.warn("Invite found, but edge lookup failed:", edgeError);
    }
  }
  return null;
};

const findInviteByToken = async (rawToken) => {
  const tokens = inviteTokenCandidates(rawToken);
  if (tokens.length === 0) return null;

  for (const token of tokens) {
    try {
      const invitationQueries = [
        query(collection(db, 'invitations'), where('token', '==', token)),
        query(collection(db, 'invitations'), where('inviteCode', '==', token)),
      ];
      for (const invitationQuery of invitationQueries) {
        const invitationSnap = await getDocs(invitationQuery);
        if (!invitationSnap.empty) {
          const invitationDoc = invitationSnap.docs[0];
          const bubbleId = invitationDoc.data()?.bubbleId;
          const edgeDoc = bubbleId ? await findEdgeInBubble(bubbleId, tokens) : null;
          return invitationFromDoc(invitationDoc, edgeDoc);
        }
      }
    } catch (invitationError) {
      console.warn("Invitation lookup failed:", invitationError);
    }
  }

  for (const token of tokens) {
    const edgeQueries = [
      query(collectionGroup(db, 'edges'), where('referralToken', '==', token)),
      query(
        collectionGroup(db, 'edges'),
        where('referralToken', '==', token),
        where('accepted', '==', false)
      ),
    ];
    for (const edgeQuery of edgeQueries) {
      try {
        const edgeSnap = await getDocs(edgeQuery);
        console.log("Query executed, found", edgeSnap.size, "edges for token", token);
        if (!edgeSnap.empty) {
          return edgeInviteFromDoc(edgeSnap.docs[0]);
        }
      } catch (edgeError) {
        console.warn("Edge token query failed:", edgeError);
      }
    }
  }

  return null;
};

const STORED_BUBBLE_ID_KEY = 'familyBubble_bubbleId';
const STORED_BUBBLE_USER_KEY = 'familyBubble_bubbleUserId';

export const sessionBubble = {
  get(userId) {
    try {
      const storedId = localStorage.getItem(STORED_BUBBLE_ID_KEY);
      const storedUser = localStorage.getItem(STORED_BUBBLE_USER_KEY);
      if (!storedId) return null;
      if (userId && storedUser && storedUser !== userId) {
        localStorage.removeItem(STORED_BUBBLE_ID_KEY);
        localStorage.removeItem(STORED_BUBBLE_USER_KEY);
        return null;
      }
      return storedId;
    } catch (error) {
      return null;
    }
  },
  set(userId, bubbleId) {
    try {
      if (!bubbleId) return;
      localStorage.setItem(STORED_BUBBLE_ID_KEY, bubbleId);
      if (userId) localStorage.setItem(STORED_BUBBLE_USER_KEY, userId);
    } catch (error) {
      console.warn("Could not persist bubble id:", error);
    }
  },
  clear() {
    try {
      localStorage.removeItem(STORED_BUBBLE_ID_KEY);
      localStorage.removeItem(STORED_BUBBLE_USER_KEY);
    } catch (error) {
      // ignore
    }
  },
};

export const API = {
  createBubble: async (
    userId,
    firstName,
    lastName,
    bubbleName,
    imageFile,
    relationshipRole,
    location = null,
    { isPremium = false } = {}
  ) => {
    console.log("Creating bubble for user:", userId);
    
    // Create/update user document FIRST (before batch to ensure it exists)
    const userRef = doc(db, 'users', userId);
    const existingUserDoc = await getDoc(userRef);
    
    let uploadedPhotoUrl = null;
    
    // Create user document if it doesn't exist
    if (!existingUserDoc.exists()) {
      console.log("Creating new user document for:", userId);
      try {
        const user = new User(
          userId,
          `${firstName} ${lastName}`,
          uploadedPhotoUrl || null,
          serverTimestamp(),
          true, // User has just completed the signup/purchase flow
          []
        );
        await setDoc(userRef, user.toFirestore());
        console.log("User document created successfully");
        
        // Verify it was created
        const verifyDoc = await getDoc(userRef);
        if (!verifyDoc.exists()) {
          throw new Error("Failed to create user document - document does not exist after creation");
        }
        console.log("User document verified:", verifyDoc.id);
      } catch (userError) {
        console.error("Error creating user document:", userError);
        throw new Error(`Failed to create user document: ${userError.message}`);
      }
    } else {
      console.log("User document already exists for:", userId);
      const existingUser = User.fromFirestore(existingUserDoc);
      if ((existingUser.bubbles || []).length >= MAX_USER_BUBBLES) {
        throw new Error('You can be in 2 bubbles at a time, including the one you are in.');
      }
      if (uploadedPhotoUrl && uploadedPhotoUrl !== existingUser.photoURL) {
        existingUser.photoURL = uploadedPhotoUrl;
        existingUser.fullName = `${firstName} ${lastName}`;
        await updateDoc(userRef, {
          photoURL: existingUser.photoURL,
          fullName: existingUser.fullName,
        });
      }
    }

    // Create the bubble, then membership, then the owner node.
    // These cannot share one batch: rules evaluate get()/exists() against
    // the pre-commit state, so the first node would be denied.
    const bubbleRef = doc(collection(db, 'bubbles'));
    const bubble = new Bubble(
      bubbleRef.id,
      userId,
      bubbleName || `${firstName}'s Bubble`,
      serverTimestamp(),
      memberLimitForPlan(isPremium), // maxMembers — free vs premium
      null, // inviteCode - will be generated later if needed
      'private', // visibility
      [userId] // members - Add owner to members list
    );

    // 2. Create the Node document for the owner in the subcollection
    // Owner is tier 1, center of the universe, root node
    const nodeRef = doc(collection(db, 'bubbles', bubbleRef.id, 'nodes'));
    
    // Prepare location data with timestamp if provided
    let locationData = null;
    if (location) {
      locationData = {
        latitude: location.latitude,
        longitude: location.longitude,
        timestamp: serverTimestamp(),
        accuracy: location.accuracy || null,
        address: location.address || null, // Store address if provided
      };
    }
    
    const node = new BubbleNode(
      nodeRef.id,
      userId,
      bubbleRef.id,
      relationshipRole || 'Owner',
      'Safe',
      null, // quote
      1, // tier - Owner is always tier 1 - the sun
      'owner',
      serverTimestamp(),
      serverTimestamp(),
      uploadedPhotoUrl,
      `${firstName} ${lastName}`,
      locationData
    );

    console.log("Creating bubble - bubbleId:", bubbleRef.id, "userId:", userId);
    try {
      await setDoc(bubbleRef, bubble.toFirestore());
      console.log("Bubble document created:", bubbleRef.id);

      await setDoc(userRef, {
        bubbles: arrayUnion(bubbleRef.id)
      }, { merge: true });
      console.log("User membership updated for bubble:", bubbleRef.id);

      await setDoc(nodeRef, node.toFirestore());
      console.log("Owner node created:", nodeRef.id);
      
      // Verify bubble was created
      const verifyBubbleDoc = await getDoc(bubbleRef);
      if (!verifyBubbleDoc.exists()) {
        throw new Error("Bubble was not created - document does not exist after commit");
      }
      console.log("Bubble verified:", verifyBubbleDoc.id);
      
      // Verify node was created
      const verifyNodeDoc = await getDoc(nodeRef);
      if (!verifyNodeDoc.exists()) {
        throw new Error("Node was not created - document does not exist after commit");
      }
      console.log("Node verified:", verifyNodeDoc.id);

      if (imageFile) {
        try {
          uploadedPhotoUrl = await uploadImage(imageFile, userId);
          if (uploadedPhotoUrl) {
            await setDoc(userRef, { photoURL: uploadedPhotoUrl }, { merge: true });
            await setDoc(nodeRef, { photoUrl: uploadedPhotoUrl }, { merge: true });
            console.log("Photo attached after bubble create");
          }
        } catch (photoError) {
          console.error("Photo upload after create failed; bubble is already saved:", photoError);
        }
      }
      
      return { bubbleId: bubbleRef.id, nodeId: nodeRef.id };
    } catch (batchError) {
      console.error("Error creating bubble:", batchError);
      throw new Error(`Failed to create bubble: ${batchError.message}`);
    }
  },

  getUserBubble: async (userId) => {
    // Get the user's primary bubble (first one they own or joined)
    // Multi-universe: A user can own multiple bubbles and join multiple bubbles
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      console.log("User document does not exist for:", userId);
      return null; // User doesn't exist yet
    }
    
    const user = User.fromFirestore(userDoc);
    if (!user.bubbles || user.bubbles.length === 0) {
      console.log("User has no bubbles yet:", userId);
      return null; // No bubbles for this user
    }
    
    const bubbleIds = [...new Set((user.bubbles || []).filter(Boolean))];
    const preferredId = sessionBubble.get(userId);
    const orderedIds = preferredId && bubbleIds.includes(preferredId)
      ? [preferredId, ...bubbleIds.filter((id) => id !== preferredId)]
      : bubbleIds;

    for (const bubbleId of orderedIds) {
      const data = await API.getBubbleById(bubbleId, userId);
      if (data?.bubble) {
        sessionBubble.set(userId, bubbleId);
        console.log("Loading bubble for user:", userId, "bubbleId:", bubbleId);
        return data;
      }
      console.warn("Skipping missing or unreadable bubble:", bubbleId);
    }

    if (preferredId && !bubbleIds.includes(preferredId)) {
      sessionBubble.clear();
    }
    return null;
  },

  getUserBubbles: async (userId) => {
    // Multi-universe support: Get all bubbles a user is part of
    // Returns both owned bubbles (where user is tier 1/owner) and joined bubbles
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      return []; // No user document
    }
    
    const user = User.fromFirestore(userDoc);
    if (!user.bubbles || user.bubbles.length === 0) {
      return []; // No bubbles for this user
    }
    
    const bubbleIds = user.bubbles;
    const bubbles = await Promise.all(
      bubbleIds.map(async (bubbleId) => {
        const bubbleData = await API.getBubbleById(bubbleId, userId);
        if (bubbleData) {
          // Determine if user is owner or participant
          const isOwner = bubbleData.currentMember?.type === 'owner' || bubbleData.currentMember?.tier === 1;
          return {
            ...bubbleData,
            isOwner,
            bubbleId: bubbleId,
          };
        }
        return null;
      })
    );
    
    return bubbles.filter(b => b !== null);
  },

  getBubbleById: async (bubbleId, memberId = null) => {
    if (!bubbleId) {
      console.error("getBubbleById called without bubbleId");
      return null;
    }

    // Get the bubble document
    const bubbleRef = doc(db, 'bubbles', bubbleId);
    const bubbleDoc = await getDoc(bubbleRef);
    
    if (!bubbleDoc.exists()) {
      console.warn("Bubble document does not exist:", bubbleId);
      return null;
    }
    
    const bubble = Bubble.fromFirestore(bubbleDoc);
    console.log("Found bubble:", bubble.bubbleId, "name:", bubble.name);

    const nodesRef = collection(db, 'bubbles', bubbleId, 'nodes');
    let nodesSnapshot = null;
    let listError = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        nodesSnapshot = await getDocs(nodesRef);
        listError = null;
        break;
      } catch (error) {
        listError = error;
        console.warn(`Listing bubble members failed (attempt ${attempt + 1}):`, error.code || error.message);
        if (error.code !== 'permission-denied' || attempt === 2) {
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
      }
    }
    if (!nodesSnapshot) {
      throw listError || new Error('Failed to load bubble members.');
    }
    const allMembers = nodesSnapshot.docs.map(d => {
      const node = BubbleNode.fromFirestore(d);
      return { id: node.nodeId, ...node };
    });
    
    console.log("Found", allMembers.length, "members in bubble");

    // Find the current member if memberId provided
    let currentMember = null;
    if (memberId) {
      // First try to match by userId (most reliable)
      currentMember = allMembers.find(m => m.userId === memberId);
      
      // If not found, try matching by node id
      if (!currentMember) {
        currentMember = allMembers.find(m => m.id === memberId);
      }
      
      if (currentMember) {
        console.log("Found current member:", currentMember.name, "userId:", currentMember.userId);
      } else {
        console.warn("Current member not found. Looking for userId:", memberId, "Available members:", allMembers.map(m => ({ id: m.id, userId: m.userId, name: m.name })));
      }
    }

    // Fallback to first member if current member not found
    if (!currentMember && allMembers.length > 0) {
      currentMember = allMembers[0];
      console.log("Using first member as fallback:", currentMember.name);
    }

    return {
      bubble: { id: bubble.bubbleId, ...bubble },
      allMembers,
      currentMember: currentMember || null,
    };
  },
  
  generateReferral: async (bubbleId, fromNodeId, invitee = null) => {
    const token = `BUB${Math.random().toString(36).substring(2, 11).toUpperCase()}`;
    const fromNodeRef = doc(db, 'bubbles', bubbleId, 'nodes', fromNodeId);
    const fromNodeDoc = await getDoc(fromNodeRef);

    if (!fromNodeDoc.exists()) {
      throw new Error('Referrer node not found.');
    }

    const fromNode = BubbleNode.fromFirestore(fromNodeDoc);
    const referrerTier = fromNode.tier || 1;
    const newMemberTier = referrerTier + 1;
    const createdBy = auth.currentUser?.uid || fromNode.userId;
    if (!createdBy) {
      throw new Error('You must be signed in to generate an invite.');
    }

    const bubbleRef = doc(db, 'bubbles', bubbleId);
    const bubbleSnap = await getDoc(bubbleRef);
    if (bubbleSnap.exists()) {
      const bubbleData = bubbleSnap.data() || {};
      const memberCount = Array.isArray(bubbleData.members) ? bubbleData.members.length : 0;
      if (!canJoinAtMemberCap({ memberCount, maxMembers: bubbleData.maxMembers })) {
        throw new Error(joinLimitMessage());
      }
    }

    const edgePayload = {
      fromNode: fromNodeId,
      toNode: null,
      relationship: '',
      referralToken: token,
      accepted: false,
      tier: newMemberTier,
      createdAt: serverTimestamp(),
      acceptedAt: null,
    };

    let edgeRef = null;
    let invitationRef = null;
    let persistError = null;

    try {
      edgeRef = await addDoc(collection(db, 'bubbles', bubbleId, 'edges'), edgePayload);
    } catch (edgeError) {
      persistError = edgeError;
      console.error("Edge invite write failed; storing invitation anyway:", edgeError);
    }

    try {
      invitationRef = await addDoc(collection(db, 'invitations'), {
        bubbleId,
        createdBy,
        createdAt: serverTimestamp(),
        token,
        fromNodeId,
        accepted: false,
        used: false,
        ...(invitee?.userId ? {
          inviteeUserId: invitee.userId,
          inviteeName: invitee.name || null,
        } : {}),
      });
    } catch (invitationError) {
      persistError = invitationError;
      console.error("Invitation write failed:", invitationError);
    }

    if (!edgeRef && !invitationRef) {
      throw new Error(`Failed to save invite code: ${persistError?.message || 'unknown error'}`);
    }

    return {
      token,
      edgeId: edgeRef?.id || null,
      invitationId: invitationRef?.id || null,
      tier: newMemberTier,
    };
  },

  joinBubble: async (token, userName, userPhotoFile, relationshipRole, userId = null, location = null) => {
    if (!userId) {
      throw new Error('User must be authenticated to join a bubble.');
    }

    console.log("=== JOIN BUBBLE START ===");
    console.log("userId:", userId);
    console.log("userName:", userName);
    console.log("token:", token);
    console.log("relationshipRole:", relationshipRole);

    // ============================================================================
    // STEP 1: CREATE USER DOCUMENT IN FIRESTORE FIRST (CRITICAL!)
    // This MUST happen regardless of whether the join succeeds or fails
    // ============================================================================
    const userRef = doc(db, 'users', userId);
    let existingUserDoc = null;
    let uploadedPhotoUrl = null;
    
    try {
      existingUserDoc = await getDoc(userRef);
      console.log("Checked for existing user document:", existingUserDoc.exists());
    } catch (checkError) {
      console.error("Error checking for existing user:", checkError);
      throw new Error(`Failed to check user document: ${checkError.message}`);
    }

    // Do not upload photos before join. Storage CORS/billing failures would
    // block writing the user and bubble membership.
    
    if (!existingUserDoc || !existingUserDoc.exists()) {
      console.log("*** CREATING USER DOCUMENT IN FIRESTORE ***");
      console.log("Collection: users");
      console.log("Document ID:", userId);
      console.log("Full path: users/", userId);
      
      try {
        const user = new User(
          userId,
          userName,
          uploadedPhotoUrl || null,
          serverTimestamp(),
          false, // premium
          [] // bubbles
        );
        console.log("User data to create:", JSON.stringify(user.toFirestore(), null, 2));
        
        await setDoc(userRef, user.toFirestore());
        console.log("✓ setDoc() completed - User document should be created");
        
        // Wait a moment for Firestore to propagate
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Verify it was created
        const verifyDoc = await getDoc(userRef);
        if (!verifyDoc.exists()) {
          console.error("✗ VERIFICATION FAILED: User document does not exist after creation!");
          console.error("Attempted path: users/", userId);
          throw new Error("Failed to create user document - document does not exist after creation");
        }
        console.log("✓ User document verified in Firestore");
        console.log("✓ Document ID:", verifyDoc.id);
        console.log("✓ Document data:", JSON.stringify(verifyDoc.data(), null, 2));
      } catch (userError) {
        console.error("✗ ERROR creating user document:", userError);
        console.error("Error code:", userError.code);
        console.error("Error message:", userError.message);
        console.error("Error stack:", userError.stack);
        // Don't throw here - we want to continue and see if the rest works
        // But log it prominently
        console.error("⚠️⚠️⚠️ USER DOCUMENT CREATION FAILED - THIS IS A CRITICAL ERROR ⚠️⚠️⚠️");
      }
    } else {
      console.log("User document already exists in Firestore");
      const existingUser = User.fromFirestore(existingUserDoc);
      console.log("Existing user data:", JSON.stringify(existingUser, null, 2));
      
      if (uploadedPhotoUrl && uploadedPhotoUrl !== existingUser.photoURL) {
        try {
          existingUser.photoURL = uploadedPhotoUrl;
          await updateDoc(userRef, { photoURL: existingUser.photoURL });
          console.log("Updated user photo URL");
        } catch (updateError) {
          console.error("Error updating user photo:", updateError);
        }
      } else if (existingUser.photoURL) {
        uploadedPhotoUrl = existingUser.photoURL;
        console.log("Using existing photo URL:", uploadedPhotoUrl);
      }
    }

    console.log("=== STEP 2: Finding invite with token ===");
    const invite = await findInviteByToken(token);
    if (!invite || !invite.bubbleId) {
      console.error("No invite found with token:", normalizeInviteToken(token));
      throw new Error('Invalid or expired invite code.');
    }
    if (invite.accepted) {
      throw new Error(INVITE_ALREADY_USED);
    }

    const bubbleId = invite.bubbleId;
    const edgeDoc = invite.edgeDoc;
    const edge = edgeDoc ? BubbleEdge.fromFirestore(edgeDoc) : null;
    const fromNodeId = invite.fromNodeId || edge?.fromNode;
    console.log("Found invite for bubble:", bubbleId, "fromNode:", fromNodeId);

    if (!fromNodeId) {
      throw new Error('This invite is missing the inviter. Ask them to generate a new code.');
    }

    const fromNodeRef = doc(db, 'bubbles', bubbleId, 'nodes', fromNodeId);
    const fromNodeDoc = await getDoc(fromNodeRef);

    if (!fromNodeDoc.exists()) {
      console.error("Referrer node not found:", fromNodeId);
      throw new Error('Referrer node not found. The invite may be invalid.');
    }

    const fromNode = BubbleNode.fromFirestore(fromNodeDoc);
    const referrerTier = fromNode.tier || 1;
    const newMemberTier = edge?.tier || (referrerTier + 1);
    console.log("Referrer tier:", referrerTier, "New member tier:", newMemberTier);
    
    const bubbleRef = doc(db, 'bubbles', bubbleId);
    const bubbleDoc = await getDoc(bubbleRef);
    
    if (!bubbleDoc.exists()) {
      console.error("Bubble not found:", bubbleId);
      throw new Error('Bubble not found. The invite code may be invalid.');
    }

    console.log("Found bubble:", bubbleId, "for user:", userId);

    const memberUserDoc = await getDoc(userRef);
    const memberBubbles = memberUserDoc.exists()
      ? (memberUserDoc.data()?.bubbles || [])
      : [];
    if (memberBubbles.includes(bubbleId)) {
      return { bubbleId, alreadyMember: true };
    }
    if (memberBubbles.length >= MAX_USER_BUBBLES) {
      throw new Error('You can be in 2 bubbles at a time. Leave one to join another.');
    }
    const existingMembers = Array.isArray(bubbleDoc.data()?.members) ? bubbleDoc.data().members : [];
    if (!existingMembers.includes(userId) && !canJoinAtMemberCap({
      memberCount: existingMembers.length,
      maxMembers: bubbleDoc.data()?.maxMembers,
    })) {
      throw new Error(joinLimitMessage());
    }

    // Create a new node for the user in the bubble
    const nodeRef = doc(collection(db, 'bubbles', bubbleId, 'nodes'));
    
    // Prepare location data with timestamp if provided
    let locationData = null;
    if (location) {
      locationData = {
        latitude: location.latitude,
        longitude: location.longitude,
        timestamp: serverTimestamp(),
        accuracy: location.accuracy || null,
        address: location.address || null,
      };
    }
    
    const node = new BubbleNode(
      nodeRef.id,
      userId,
      bubbleId,
      relationshipRole || 'Family Member',
      'Safe',
      null, // quote
      newMemberTier,
      'participant',
      serverTimestamp(),
      serverTimestamp(),
      uploadedPhotoUrl,
      userName,
      locationData
    );

    // Record membership, the new node, and consume the invite in one commit
    // so a second person cannot redeem the same code.
    try {
      await runTransaction(db, async (transaction) => {
        const invitationSnap = invite.invitationDoc
          ? await transaction.get(invite.invitationDoc.ref)
          : null;
        const edgeSnap = invite.edgeDoc
          ? await transaction.get(invite.edgeDoc.ref)
          : null;

        if (inviteFromRecords(invitationSnap?.data() || {}, edgeSnap?.data() || null)) {
          throw new Error(INVITE_ALREADY_USED);
        }
        if (!invitationSnap?.exists() && !edgeSnap?.exists()) {
          throw new Error('Invalid or expired invite code.');
        }

        transaction.update(userRef, {
          bubbles: arrayUnion(bubbleId),
        });
        transaction.set(nodeRef, node.toFirestore());
        transaction.update(bubbleRef, {
          members: arrayUnion(userId),
        });
        if (edgeSnap?.exists()) {
          transaction.update(edgeSnap.ref, {
            toNode: nodeRef.id,
            accepted: true,
            acceptedAt: serverTimestamp(),
            tier: newMemberTier,
          });
        }
        if (invitationSnap?.exists()) {
          transaction.update(invitationSnap.ref, {
            accepted: true,
            used: true,
            acceptedBy: userId,
            acceptedAt: serverTimestamp(),
          });
        }
      });
      console.log("✓ Join committed and invite code consumed");
      
      // Wait a moment for Firestore to propagate
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Verify user document still exists and has bubble
      const verifyUserDoc = await getDoc(userRef);
      if (!verifyUserDoc.exists()) {
        console.error("✗ ERROR: User document does not exist after batch commit!");
        throw new Error("User document was deleted or not created properly");
      }
      
      const userData = verifyUserDoc.data();
      const userBubbles = userData.bubbles || [];
      const hasBubble = userBubbles.includes(bubbleId);
      
      console.log("✓ User document verified after batch:");
      console.log("  - Document ID:", verifyUserDoc.id);
      console.log("  - Full Name:", userData.fullName);
      console.log("  - Bubbles array:", userBubbles);
      console.log("  - Bubbles array length:", userBubbles.length);
      console.log("  - Target bubbleId:", bubbleId);
      console.log("  - Has bubble in array:", hasBubble);
      
      if (!hasBubble) {
        console.error("✗ CRITICAL: Bubble was NOT added to user's bubbles array!");
        console.error("  Expected bubbleId:", bubbleId);
        console.error("  Actual bubbles array:", userBubbles);
        // FIX: Manually add the bubble to the user's array
        console.log("🔧 FIXING: Manually adding bubble to user's bubbles array...");
        try {
          await updateDoc(userRef, {
            bubbles: arrayUnion(bubbleId)
          });
          console.log("✓ Manually added bubble to user's bubbles array");
          
          // Verify again
          await new Promise(resolve => setTimeout(resolve, 200));
          const verifyAgain = await getDoc(userRef);
          const verifyData = verifyAgain.data();
          const verifyBubbles = verifyData.bubbles || [];
          console.log("✓ Verification - bubbles array now:", verifyBubbles);
          if (verifyBubbles.includes(bubbleId)) {
            console.log("✓ SUCCESS: Bubble is now in user's bubbles array");
          } else {
            console.error("✗ STILL FAILED: Bubble still not in array after manual fix");
          }
        } catch (fixError) {
          console.error("✗ Error manually fixing bubbles array:", fixError);
        }
      } else {
        console.log("✓ Bubble successfully added to user's bubbles array");
      }
      
      // Also verify the node was created
      const verifyNodeDoc = await getDoc(nodeRef);
      if (!verifyNodeDoc.exists()) {
        console.error("✗ ERROR: Node document does not exist after batch commit!");
        throw new Error("Node document was not created properly");
      }
      console.log("✓ Node document verified:", verifyNodeDoc.id);
      
      // Verify bubble has the user in members
      const verifyBubbleDoc = await getDoc(bubbleRef);
      if (verifyBubbleDoc.exists()) {
        const bubbleData = verifyBubbleDoc.data();
        const bubbleMembers = bubbleData.members || [];
        const userInMembers = bubbleMembers.includes(userId);
        console.log("✓ Bubble members array:", bubbleMembers);
        console.log("✓ User in bubble members:", userInMembers);
        if (!userInMembers) {
          console.error("✗ WARNING: User not in bubble's members array!");
        }
      }

      if (userPhotoFile) {
        try {
          uploadedPhotoUrl = await uploadImage(userPhotoFile, userId);
          if (uploadedPhotoUrl) {
            await updateDoc(userRef, { photoURL: uploadedPhotoUrl });
            await updateDoc(nodeRef, { photoUrl: uploadedPhotoUrl });
            console.log("Photo attached after join");
          }
        } catch (photoError) {
          console.error("Photo upload after join failed; membership is already saved:", photoError);
        }
      }
      
      return { bubbleId, nodeId: nodeRef.id, nodeUserId: userId };
    } catch (batchError) {
      if (batchError?.message === INVITE_ALREADY_USED) {
        throw batchError;
      }
      console.error("✗ Error committing join:", batchError);
      console.error("Error code:", batchError.code);
      console.error("Error message:", batchError.message);
      throw new Error(`Failed to join bubble: ${batchError.message}`);
    }
  },

  updateStatus: async (bubbleId, nodeId, status, statusText = null, options = {}) => {
    const nodeRef = doc(db, 'bubbles', bubbleId, 'nodes', nodeId);
    const updateData = { status, lastUpdated: serverTimestamp() };
    if (statusText !== null) {
      updateData.statusText = statusText;
    }

    const photoFiles = Array.isArray(options.photoFiles)
      ? options.photoFiles.filter(Boolean)
      : (options.photoFile ? [options.photoFile] : []);
    const mediaPromise = (!options.skipMemo && (photoFiles.length || options.voiceBlob))
      ? prepareStatusMemoMedia({
          photoFile: photoFiles[0] || null,
          photoFiles,
          voiceBlob: options.voiceBlob || null,
          userId: auth.currentUser?.uid,
        }).catch((error) => {
          console.warn('Status memo media skipped:', error.message);
          return { photoUrl: null, photoUrls: [], voiceUrl: null };
        })
      : Promise.resolve({ photoUrl: null, photoUrls: [], voiceUrl: null });

    await updateDoc(nodeRef, updateData);

    // Family Memos board: every status update is posted to this bubble only.
    if (!options.skipMemo) {
      const [nodeSnap, media] = await Promise.all([getDoc(nodeRef), mediaPromise]);
      const node = nodeSnap.exists() ? nodeSnap.data() : {};
      await createFamilyMemo({
        bubbleId,
        userId: node.userId || auth.currentUser?.uid,
        nodeId,
        type: MEMO_TYPE.STATUS,
        status,
        message: statusText,
        location: options.location || node.lastKnownLocation || null,
        photoUrl: media.photoUrl,
        photoUrls: media.photoUrls,
        voiceUrl: media.voiceUrl,
        voiceDurationMs: options.voiceDurationMs || null,
      }).catch((error) => {
        console.warn('Family memo write skipped:', error.message);
      });
    }
    return { success: true };
  },

  updatePhoto: async (bubbleId, nodeId, userId, imageFile) => {
    if (!imageFile) {
      throw new Error('No image file provided');
    }
    if (!userId) {
      throw new Error('User ID is required');
    }

    // Upload the new photo
    let uploadedPhotoUrl = null;
    try {
      uploadedPhotoUrl = await uploadImage(imageFile, userId);
      if (!uploadedPhotoUrl) {
        throw new Error('Photo upload failed');
      }
      console.log("Photo uploaded successfully, length:", uploadedPhotoUrl.length);
    } catch (uploadError) {
      console.error("Photo upload failed:", uploadError);
      throw new Error(`Failed to upload photo: ${uploadError.message}`);
    }

    // Update both the node and user document
    const batch = writeBatch(db);
    
    // Update the node's photo
    const nodeRef = doc(db, 'bubbles', bubbleId, 'nodes', nodeId);
    batch.update(nodeRef, {
      photoUrl: uploadedPhotoUrl,
      lastUpdated: serverTimestamp(),
    });

    // Update the user's photo (global profile)
    const userRef = doc(db, 'users', userId);
    batch.update(userRef, {
      photoURL: uploadedPhotoUrl,
    });

    await batch.commit();
    console.log("Photo updated successfully for node and user");
    return { success: true, photoUrl: uploadedPhotoUrl };
  },

  updateProfile: async (bubbleId, nodeId, profileData) => {
    if (!profileData || !profileData.name) {
      throw new Error('Name is required');
    }

    const nodeRef = doc(db, 'bubbles', bubbleId, 'nodes', nodeId);
    const nodeDoc = await getDoc(nodeRef);
    
    if (!nodeDoc.exists()) {
      throw new Error('Node not found');
    }

    const node = BubbleNode.fromFirestore(nodeDoc);
    
    // Update the node with new profile data
    await updateDoc(nodeRef, {
      name: profileData.name,
      role: profileData.role || node.role,
      quote: profileData.quote || null,
      phone: profileData.phone || null,
      lastUpdated: serverTimestamp(),
    });

    // Also update the user document's fullName if it's the current user's node
    if (node.userId) {
      const userRef = doc(db, 'users', node.userId);
      await updateDoc(userRef, {
        fullName: profileData.name,
      });
    }

    console.log("Profile updated successfully");
    return { success: true };
  },

  updateLocation: async (bubbleId, nodeId, location) => {
    if (!location || !location.latitude || !location.longitude) {
      throw new Error('Valid location data is required');
    }

    const nodeRef = doc(db, 'bubbles', bubbleId, 'nodes', nodeId);
    
    // Update location with timestamp
    const locationData = {
      latitude: location.latitude,
      longitude: location.longitude,
      timestamp: serverTimestamp(),
      accuracy: location.accuracy || null,
      address: location.address || null, // Store address if provided
    };

    await updateDoc(nodeRef, {
      lastKnownLocation: locationData,
      lastUpdated: serverTimestamp(),
    });

    getPlaceWatcher().ingestConfirmed(location).catch((error) => {
      console.warn('Place presence sync failed:', error);
    });

    console.log("Location updated successfully");
    return { success: true };
  },

  /**
   * Check in: drop the member's live GPS on the map and post a family memo.
   * Does not change their status emoji.
   */
  checkIn: async (bubbleId, nodeId, location) => {
    if (!location || location.latitude == null || location.longitude == null) {
      throw new Error('Location is required to check in.');
    }
    const uid = auth.currentUser?.uid;
    if (!uid) {
      throw new Error('You must be signed in to check in.');
    }

    await API.updateLocation(bubbleId, nodeId, location);
    const memo = buildCheckInMemo({ location });
    await createFamilyMemo({
      bubbleId,
      userId: uid,
      nodeId,
      type: memo.type,
      status: memo.status,
      message: memo.message,
      location,
    });
    return { success: true, location };
  },
  
  leaveBubble: async (bubbleId, nodeId, userId) => {
    const batch = writeBatch(db);

    // Remove the node
    const nodeRef = doc(db, 'bubbles', bubbleId, 'nodes', nodeId);
    batch.delete(nodeRef);

    // Remove the user from the bubble's members list
    const bubbleRef = doc(db, 'bubbles', bubbleId);
    batch.update(bubbleRef, {
        members: arrayRemove(userId),
    });

    // Remove the bubble from the user's bubbles list
    const userRef = doc(db, 'users', userId);
    batch.update(userRef, {
        bubbles: arrayRemove(bubbleId),
    });
    
    await batch.commit();
    return { success: true };
  },

  getPendingInvites: async (userId) => {
    if (!userId) return [];
    const invitesQuery = query(
      collection(db, 'invitations'),
      where('inviteeUserId', '==', userId)
    );
    const snapshot = await getDocs(invitesQuery);
    return snapshot.docs
      .map((inviteDoc) => ({ id: inviteDoc.id, ...inviteDoc.data() }))
      .filter((invite) => invite.token && !invite.used && !invite.accepted);
  },

  inviteMembersToBubble: async (targetBubbleId, fromNodeId, members = []) => {
    const unique = [];
    const seen = new Set();
    (members || []).forEach((member) => {
      if (!member?.userId || seen.has(member.userId)) return;
      seen.add(member.userId);
      unique.push(member);
    });
    const results = [];
    for (const member of unique) {
      const referral = await API.generateReferral(targetBubbleId, fromNodeId, {
        userId: member.userId,
        name: member.name || member.fullName || 'Family member',
      });
      results.push({
        ...referral,
        inviteeUserId: member.userId,
        inviteeName: member.name || member.fullName || 'Family member',
      });
    }
    return results;
  },

  applyPremiumToOwner: async (userId) => {
    if (!userId) return { success: false };
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      return { success: false };
    }

    try {
      await updateDoc(userRef, { premium: true });
    } catch (error) {
      console.error('Failed to mark user premium:', error);
    }

    const bubbleIds = userSnap.data()?.bubbles || [];
    await Promise.all(bubbleIds.map(async (bubbleId) => {
      const bubbleRef = doc(db, 'bubbles', bubbleId);
      const bubbleSnap = await getDoc(bubbleRef);
      if (!bubbleSnap.exists()) return;
      if (bubbleSnap.data()?.ownerId !== userId) return;
      await updateDoc(bubbleRef, { maxMembers: memberLimitForPlan(true) });
    }));

    return { success: true };
  },

  // ============================================================================
  // TIER-BASED UTILITIES (For future expansion)
  // ============================================================================
  
  /**
   * Get visibility level based on tier
   * Tier 1: Full access (owner)
   * Tier 2: Full visibility, limited control
   * Tier 3: Limited visibility
   * Tier 4+: Preview only
   */
  getTierVisibility: (tier) => {
    if (tier === 1) return 'full'; // Owner - sees everything
    if (tier === 2) return 'full'; // Immediate family - sees all
    if (tier === 3) return 'limited'; // Extended family - filtered view
    return 'preview'; // Tier 4+ - preview only
  },

  /**
   * Check if a user can perform an action based on their tier
   */
  canPerformAction: (userTier, action) => {
    // Tier 1 (owner) can do everything
    if (userTier === 1) return true;
    
    // Tier 2 can invite and update status
    if (userTier === 2) {
      return ['invite', 'updateStatus', 'view'].includes(action);
    }
    
    // Tier 3+ can only view and update own status
    if (userTier === 3) {
      return ['updateStatus', 'view'].includes(action);
    }
    
    // Tier 4+ can only view
    return action === 'view';
  },
};